import { useEffect, useState } from 'react';
import { CREATION_ID } from '../../editor/editorReducer';
import useUpdateAnnotation from './useUpdateAnnotation';

const ALLOWED_PROTOCOLS = ['https:', 'http:', 'mailto:', 'tel:', 'webcal:'];

function sanitizeAnnotationUrl(value: string) {
  const anchor = document.createElement('a');
  anchor.href = value;

  if (ALLOWED_PROTOCOLS.includes(anchor.protocol)) {
    if (!value.startsWith(anchor.protocol) && !value.startsWith('#')) {
      return `https://${value}`;
    }
  }

  return value;
}

interface UseAnnotationLinkProps {
  id: string;
  initialValue: string;
  onUpdated(): void;
}

function useAnnotationLink({
  id,
  initialValue,
  onUpdated,
}: UseAnnotationLinkProps) {
  const isNewAnnotation = id === CREATION_ID;
  const [linkValue, setLinkValue] = useState<string>(initialValue ?? '');
  const hasLink = linkValue.trim() !== '';

  const [updateAnnotation] = useUpdateAnnotation();

  useEffect(() => {
    setLinkValue(initialValue);
  }, [initialValue]);

  const onSave = async () => {
    if (linkValue.trim() !== '') {
      const link = sanitizeAnnotationUrl(linkValue.trim());

      if (!isNewAnnotation) {
        await updateAnnotation({ variables: { id, link } });
      }
      setLinkValue(link);
    } else {
      setLinkValue('');
    }

    onUpdated();
  };

  const onRemove = async () => {
    if (!isNewAnnotation) {
      await updateAnnotation({
        variables: { id, link: null },
      });
    }
    setLinkValue('');
    onUpdated();
  };

  return {
    onSave,
    onRemove,
    onChange: setLinkValue,
    link: hasLink ? linkValue : null,
    value: linkValue,
  };
}

export default useAnnotationLink;
